import classNames from "classnames";
import { observer } from "mobx-react";
import React from "react";
import { EditInput } from "@fidget/common/components/form/editInput";
import { InputGroup } from "@fidget/common/components/form/inputGroup";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { emailIsGood } from "@fidget/common/utils";
import { extractNamesFromEmail } from "@fidget/common/utils/extractNamesFromEmail";
import { useStore } from "../../../../../models";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { InviteAdvisersForm, NewAdviser } from "../../types";
import { createInviteFormAdviser } from "../../utils/utils";
import styles from "./inviteAgentBox.css";

interface Props {
  removeFromInvitees(adviser: NewAdviser): void;
  onSave(): void;
  advisers: InviteAdvisersForm;
}

export const InviteAgentBox = observer(({ advisers, removeFromInvitees, onSave }: Props) => {
  const {
    paymentStore: { taxRate },
    currentUserStore: { planPrice, planCurrency }
  } = useStore();
  const unitVat = taxRate ? Math.round(planPrice * taxRate * 100) / 100 : planPrice;
  const planPriceWithVat = planPrice + unitVat;

  const shouldRenderPrice = planPriceWithVat > 0 && !!planCurrency;

  const isEveryEmailEmpty = advisers.$.every((agent) => agent.$.email.$ === "");
  const isAnyEmailEmpty = advisers.$.some((agent) => agent.$.email.$ === "");

  const addAnotherField = () => {
    advisers.$.push(createInviteFormAdviser());
  };

  const handleBlurOnEmailInput = (adviser: NewAdviser) => {
    if (!adviser.$.firstName.$) {
      const { firstName, lastName } = extractNamesFromEmail(adviser.$.email.$);
      adviser.$.firstName.onChange(firstName);
      adviser.$.lastName.onChange(lastName);
    }
  };

  const renderPrice = (isOk: boolean) => {
    return (
      <div className={classNames(styles.adviserPrice, { [styles.errorGrey]: !isOk })}>
        {shouldRenderPrice && (
          <p>
            + {intl.formatNumber(planPriceWithVat, { minimumFractionDigits: 2 })} {planCurrency}
          </p>
        )}
      </div>
    );
  };

  const renderPriceSummary = () => {
    const amount = advisers.$.filter((form) => !!emailIsGood(form.$.email.$)).length;

    return (
      <>
        {amount > 1 ? (
          <div className={styles.adviserPriceSummaryHr}>
            <label>
              = {intl.formatNumber(planPriceWithVat * amount, { minimumFractionDigits: 2 })}{" "}
              {planCurrency}
            </label>
          </div>
        ) : (
          <div className={styles.adviserPriceSummaryBlank} />
        )}
      </>
    );
  };

  const renderForm = () => {
    return (
      <>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <div className={styles["invitee-container"]}>
            {advisers.$.map((adviser, i) => {
              const { email, firstName, lastName } = adviser.$;
              return (
                <div key={i} className={styles["input-container"]}>
                  {renderPrice(!!emailIsGood(email.$))}
                  <div className={styles["email-input"]}>
                    <EditInput
                      value={email.$}
                      onChange={(ev) => email.reset((ev.target as HTMLInputElement).value)}
                      placeholder={intl.formatMessage("settings.advisers.invite.email")}
                      hasError={email.hasError}
                      errorHint={email.error}
                      onBlur={() => handleBlurOnEmailInput(adviser)}
                      autoFocus
                    />
                  </div>
                  <div className={styles["name-input"]}>
                    <InputGroup>
                      <EditInput
                        value={firstName.$}
                        onChange={(ev) => firstName.reset((ev.target as HTMLInputElement).value)}
                        placeholder={intl.formatMessage("settings.advisers.invite.firstName")}
                      />
                    </InputGroup>
                  </div>
                  <div className={styles["name-input"]}>
                    <InputGroup>
                      <EditInput
                        value={lastName.$}
                        onChange={(ev) => lastName.reset((ev.target as HTMLInputElement).value)}
                        placeholder={intl.formatMessage("settings.advisers.invite.lastName")}
                      />
                    </InputGroup>
                  </div>
                  <button type={"button"} onClick={() => removeFromInvitees(adviser)}>
                    <i className="icon-close-cross" />
                  </button>
                </div>
              );
            })}
          </div>
          <AgentButton type={"submit"} disabled={isEveryEmailEmpty}>
            <L10n id={"settings.advisers.invite"} />
          </AgentButton>
        </form>
        <div className={styles.summary}>
          {shouldRenderPrice && renderPriceSummary()}
          <AgentButton
            buttonStyle={"transparent primary"}
            onClick={addAnotherField}
            disabled={isAnyEmailEmpty}
          >
            + {intl.formatMessage("settings.advisers.invite.addEmail")}
          </AgentButton>
        </div>
      </>
    );
  };

  return (
    <div className={classNames(styles.container)}>
      <div className={styles.content}>{renderForm()}</div>
    </div>
  );
});
